
import React, { useRef, useEffect, useState } from 'react';
import { Search } from 'lucide-react';
import Button from './Button';
import { useIsMobile } from '@/hooks/use-mobile';
import { Input } from './ui/input';
import { cn } from '@/lib/utils';
import { useNavigate } from 'react-router-dom';

const HeroSection = () => {
  const gridRef = useRef<HTMLDivElement>(null);
  const isMobile = useIsMobile();
  const [searchQuery, setSearchQuery] = useState('');
  const navigate = useNavigate();

  // Optional: Add parallax or subtle mouse movement effect
  useEffect(() => {
    if (isMobile || !gridRef.current) return;
    
    const handleMouseMove = (e: MouseEvent) => {
      const { clientX, clientY } = e;
      const centerX = window.innerWidth / 2;
      const centerY = window.innerHeight / 2;
      
      // Calculate distance from center (normalized)
      const moveX = (clientX - centerX) / (window.innerWidth / 2) * 15;
      const moveY = (clientY - centerY) / (window.innerHeight / 2) * 15;
      
      // Apply the translation
      if (gridRef.current) {
        gridRef.current.style.transform = `translate(${moveX}px, ${moveY}px)`;
      }
    };
    
    window.addEventListener('mousemove', handleMouseMove);
    return () => window.removeEventListener('mousemove', handleMouseMove);
  }, [isMobile]);

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    if (searchQuery.trim()) {
      navigate(`/search?q=${encodeURIComponent(searchQuery)}`);
    }
  };

  return (
    <section className="relative min-h-[80vh] flex items-center justify-center overflow-hidden">
      {/* Background elements - now full viewport width */}
      <div className="fixed inset-0 w-screen overflow-hidden pointer-events-none">
        {/* Abstract grid effect */}
        <div 
          ref={gridRef}
          className="absolute inset-0 w-full h-full transition-transform duration-[2s] ease-out"
          style={{ willChange: 'transform' }}
        >
          <div className="absolute inset-0 w-full h-full opacity-[0.03]" 
               style={{ 
                 backgroundImage: 'linear-gradient(to right, #000 1px, transparent 1px), linear-gradient(to bottom, #000 1px, transparent 1px)', 
                 backgroundSize: '60px 60px' 
               }}>
          </div>
          
          {/* Subtle gradient orbs */}
          <div className="absolute top-[20%] -left-20 w-80 h-80 bg-primary/20 rounded-full blur-[120px] animate-pulse-soft"></div>
          <div className="absolute bottom-[10%] right-[10%] w-96 h-96 bg-blue-400/10 rounded-full blur-[100px] animate-pulse-soft" style={{ animationDelay: '1s' }}></div>
        </div>
      </div>
      
      {/* Content */}
      <div className="container relative z-10 px-4 md:px-6">
        <div className="flex flex-col items-center text-center max-w-3xl mx-auto">
          {/* Removed the "AI Solution Encyclopedia" indicator that was here */}
          
          <h1 className="text-4xl md:text-5xl lg:text-6xl font-medium tracking-tighter mb-4 animate-fade-up opacity-0" style={{ animationDelay: '0.3s', animationFillMode: 'forwards' }}>
            The Collaborative <span className="text-blue-400">Encyclopedia</span> of <span className="text-blue-400">AI Solutions</span>
          </h1>
          
          <p className="text-lg md:text-xl text-muted-foreground mb-8 max-w-2xl animate-fade-up opacity-0" style={{ animationDelay: '0.5s', animationFillMode: 'forwards' }}>
            Search, learn, and contribute to a growing repository of AI solution information.
            Together we build the most comprehensive AI knowledge base.
          </p>
          
          <form 
            onSubmit={handleSearch}
            className={cn(
              "w-full max-w-2xl mb-6 animate-fade-up opacity-0 relative",
              "flex flex-col sm:flex-row items-center gap-2"
            )}
            style={{ animationDelay: '0.7s', animationFillMode: 'forwards' }}
          >
            <div className="relative w-full">
              <Input
                type="text"
                placeholder="Search for AI solutions using natural language..."
                className="w-full pr-10 h-12 text-base"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
              />
            </div>
            <Button 
              type="submit" 
              size="lg"
              className="w-full sm:w-auto"
              icon={<Search size={18} />}
            >
              Search
            </Button>
          </form>
          
          <p className="text-sm text-muted-foreground mb-4 animate-fade-up opacity-0" style={{ animationDelay: '0.9s', animationFillMode: 'forwards' }}>
            Try: "ai for writing books" or "tool to change color of clothes in image" or "ai for medical image analysis"
          </p>
        </div>
      </div>
      
      {/* Bottom fade effect - reduce height by 60% */}
      <div className="absolute bottom-0 left-0 right-0 h-12 bg-gradient-to-t from-background to-transparent"></div>
    </section>
  );
};

export default HeroSection;
